import { useMemo, useState } from 'react';

export type GridSearchPredicate<T> = (row: T, searchTerm: string) => boolean;

export interface UseGridSearchResult<T> {
  searchTerm: string;
  onSearch: (value: string) => void;
  filteredRows: T[];
}

/**
 * Owns search-term state and returns a memoised filtered view of `rows`.
 *
 * The `predicate` participates in the memo's dependency list, which means a new
 * predicate identity triggers a recompute. In this codebase that is intentional:
 * predicates typically close over redux-resolved lookup tables (e.g. paritair
 * comites, function descriptions) and must recompute when those change.
 *
 * Callers should therefore wrap `predicate` in `useCallback` with the right deps,
 * so the memo only recomputes when either the search term, the row set, or the
 * closed-over resolution data changes. An inline arrow function will defeat the
 * memo on every render.
 *
 * `alwaysInclude` marks rows that must survive any search term. An editable grid
 * appends blank rows for the user to fill in, and a blank row matches no term at
 * all: without this it would be filtered straight back out of the grid it was
 * just added to, leaving the add button looking dead — and, because it is the
 * unfiltered list that gets submitted, saved anyway and rejected by the API on a
 * row the user could not see. It is also wrapped in `useCallback` by callers.
 */
export function useGridSearch<T>(
  rows: T[],
  predicate: GridSearchPredicate<T>,
  alwaysInclude?: (row: T) => boolean,
): UseGridSearchResult<T> {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    return rows.filter(row => alwaysInclude?.(row) || predicate(row, searchTerm));
  }, [rows, searchTerm, predicate, alwaysInclude]);

  return { searchTerm, onSearch: setSearchTerm, filteredRows };
}
